import { useState, useEffect, useRef } from "react";
import { Camera, Upload, FileText, Trash2, Shield, Lock, Eye, EyeOff, X, Check, User, Building2, Briefcase, Award, Calendar as CalendarIcon, Mail, Phone, MapPin, Hash, Clock, CheckCircle, XCircle, Edit } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { SearchableMultiSelect } from "@/components/forms/SearchableMultiSelect";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/contexts/LanguageContext";
import { useProfile, useUpdateProfile, useDeleteDocument } from "@/hooks/api/use-profile";
import { useChangePassword } from "@/hooks/api/use-auth";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { ar, enUS } from "date-fns/locale";
import ReadOnlyWrapper from "@/components/ReadOnlyWrapper";
import { cn } from "@/lib/utils";

const Profile = () => {
  const { t, language, isRTL } = useLanguage();
  const { data: profileData, isLoading } = useProfile();
  const updateProfile = useUpdateProfile();
  const deleteDocument = useDeleteDocument();
  const changePassword = useChangePassword();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  const [formData, setFormData] = useState({
    company_name: "",
    email: "",
    phone: "",
    specialization_ids: [] as number[],
    address: "",
    description: "",
    license_number: "",
    tax_id: "",
  });

  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [commercialRegister, setCommercialRegister] = useState<File | null>(null);
  const [taxCertificate, setTaxCertificate] = useState<File | null>(null);
  const [ibanCertificate, setIbanCertificate] = useState<File | null>(null);
  const [zakatIncomeCertificate, setZakatIncomeCertificate] = useState<File | null>(null);
  const [socialInsuranceCertificate, setSocialInsuranceCertificate] = useState<File | null>(null);
  const [companyProfile, setCompanyProfile] = useState<File | null>(null);

  const [passwordData, setPasswordData] = useState({
    current_password: "",
    password: "",
    password_confirmation: "",
  });
  
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirmation: false,
  });

  useEffect(() => {
    if (profileData?.data) {
      const profile = profileData.data;
      setFormData({
        company_name: profile.company_name || "",
        email: profile.email || "",
        phone: profile.phone || "",
        specialization_ids: profile.specializations?.map((s: any) => s.id) || [],
        address: profile.address || "",
        description: profile.description || "",
        license_number: (profile as any).license_number || "",
        tax_id: (profile as any).tax_id || "",
      });
    }
  }, [profileData]);

  const documents = profileData?.data?.documents || [];
  const profile = profileData?.data;

  const handleProfileImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setProfileImage(file);
  };

  const handleCommercialRegisterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setCommercialRegister(file);
  };

  const handleTaxCertificateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setTaxCertificate(file);
  };

  const handleIbanCertificateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setIbanCertificate(file);
  };

  const handleZakatIncomeCertificateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setZakatIncomeCertificate(file);
  };

  const handleSocialInsuranceCertificateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setSocialInsuranceCertificate(file);
  };

  const handleCompanyProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setCompanyProfile(file);
  };

  const clearDocumentFile = (setter: React.Dispatch<React.SetStateAction<File | null>>, inputId: string) => {
    setter(null);
    const input = document.getElementById(inputId) as HTMLInputElement;
    if (input) input.value = "";
  };

  const handleDeleteDocument = (documentId: number) => {
    deleteDocument.mutate(documentId);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const submitData: Record<string, any> = { ...formData };

    if (profileImage) submitData.profile_image = profileImage;
    if (commercialRegister) submitData.commercial_register = commercialRegister;
    if (taxCertificate) submitData.tax_certificate = taxCertificate;
    if (ibanCertificate) submitData.iban_certificate = ibanCertificate;
    if (zakatIncomeCertificate) submitData.zakat_income_certificate = zakatIncomeCertificate;
    if (socialInsuranceCertificate) submitData.social_insurance_certificate = socialInsuranceCertificate;
    if (companyProfile) submitData.company_profile = companyProfile;

    updateProfile.mutate(submitData, {
      onSuccess: () => {
        setProfileImage(null);
        setCommercialRegister(null);
        setTaxCertificate(null);
        setIbanCertificate(null);
        setZakatIncomeCertificate(null);
        setSocialInsuranceCertificate(null);
        setCompanyProfile(null);
        document.querySelectorAll('input[type="file"]').forEach((input) => {
          (input as HTMLInputElement).value = "";
        });
      },
    });
  };

  const handlePasswordChange = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (passwordData.password !== passwordData.password_confirmation) {
      toast({
        title: t("auth.passwordMismatch") || "خطأ في كلمة المرور",
        description: t("auth.passwordsDoNotMatch") || "كلمة المرور الجديدة وتأكيد كلمة المرور غير متطابقين",
        variant: "destructive",
      });
      return;
    }

    if (passwordData.password.length < 8) {
      toast({
        title: t("auth.passwordTooShort") || "كلمة المرور قصيرة",
        description: t("auth.passwordMinLength") || "يجب أن تكون كلمة المرور 8 أحرف على الأقل",
        variant: "destructive",
      });
      return;
    }

    changePassword.mutate(passwordData, {
      onSuccess: () => {
        setPasswordData({ current_password: "", password: "", password_confirmation: "" });
      },
    });
  };

  const togglePasswordVisibility = (field: "current" | "new" | "confirmation") => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const documentTypeNames: Record<string, string> = {
    commercial_register: t("documents.commercialRegister") || "السجل التجاري",
    tax_certificate: t("documents.taxCertificate") || "شهادة الضريبة",
    iban_certificate: t("documents.ibanCertificate") || "شهادة IBAN",
    zakat_income_certificate: t("documents.zakatCertificate") || "شهادة الزكاة والدخل",
    social_insurance_certificate: t("documents.socialInsurance") || "شهادة التأمينات الاجتماعية",
    company_profile: t("documents.companyProfile") || "ملف الشركة التعريفي",
  };

  const getDocumentTypeName = (doc: any) => documentTypeNames[doc.type] || doc.title || doc.name || doc.type;

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: {
        label: t('common.pending') || 'قيد المراجعة',
        className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 border-yellow-200',
        icon: <Clock size={14} />
      },
      approved: {
        label: t('common.approved') || 'معتمد',
        className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 border-green-200',
        icon: <CheckCircle size={14} />
      },
      rejected: {
        label: t('common.rejected') || 'مرفوض',
        className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 border-red-200',
        icon: <XCircle size={14} />
      },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;

    return (
      <Badge className={cn("flex items-center gap-1 px-3 py-1 border", config.className)}>
        {config.icon}
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                {t("profile.title") || "الملف الشخصي"}
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                {t("profile.subtitle") || "إدارة معلومات ملفك الشخصي"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="bg-primary h-16" />
          <div className="px-8 pb-8">
            <div className="flex flex-col sm:flex-row items-center sm:items-end gap-6 -mt-8">
              <Avatar className="w-32 h-32 border-4 border-white dark:border-slate-800 shadow-xl">
                <AvatarImage src={profile?.profile_image ? `https://maroon-grouse-638711.hostingersite.com/${(profile as any).profile_image}` : undefined} alt={profile?.company_name || 'Profile'} />
                <AvatarFallback className="text-2xl font-bold bg-primary text-white">
                  {(formData.company_name || profile?.company_name || t("profile.companyName")).charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1 text-center sm:text-left">
                <div className="flex flex-col sm:flex-row items-center sm:items-end gap-3 mb-2">
                  <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
                    {profile?.company_name || t("profile.companyName")}
                  </h2>
                  {profile?.status && getStatusBadge(profile.status)}
                </div>
                <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 dark:text-slate-400">
                  <span className="flex items-center gap-1">
                    <Mail size={14} />
                    {profile?.email || "-"}
                  </span>
                  {profile?.phone && (
                    <span className="flex items-center gap-1">
                      <Phone size={14} />
                      {profile.phone}
                    </span>
                  )}
                  {profile?.address && (
                    <span className="flex items-center gap-1">
                      <MapPin size={14} />
                      {profile.address}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabbed Content Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-1 rounded-lg">
            <TabsTrigger value="overview" className="flex items-center gap-2 data-[state=active]:bg-slate-100 dark:data-[state=active]:bg-slate-700">
              <User size={16} />
              {t("profile.overview") || "نظرة عامة"}
            </TabsTrigger>
            <TabsTrigger value="edit" className="flex items-center gap-2 data-[state=active]:bg-slate-100 dark:data-[state=active]:bg-slate-700">
              <Edit size={16} />
              {t("profile.editProfile") || "تعديل الملف"}
            </TabsTrigger>
            <TabsTrigger value="documents" className="flex items-center gap-2 data-[state=active]:bg-slate-100 dark:data-[state=active]:bg-slate-700">
              <FileText size={16} />
              {t("profile.documents") || "الوثائق"} ({documents.length})
            </TabsTrigger>
            <TabsTrigger value="password" className="flex items-center gap-2 data-[state=active]:bg-slate-100 dark:data-[state=active]:bg-slate-700">
              <Lock size={16} />
              {t("profile.changePassword") || "تغيير كلمة المرور"}
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Company Info Card */}
              <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg flex items-center gap-2 text-slate-900 dark:text-slate-100">
                    <Building2 size={20} className="text-foreground" />
                    {t("profile.companyInfo") || "معلومات الشركة"}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {isLoading ? (
                    <div className="space-y-3">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-3/4" />
                    </div>
                  ) : profile ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium text-slate-500 dark:text-slate-400">
                          {t("profile.companyName")}
                        </Label>
                        <p className="text-foreground font-semibold mt-1">
                          {profile.company_name || "-"}
                        </p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-500 dark:text-slate-400">
                          {t("profile.email") || "البريد الإلكتروني"}
                        </Label>
                        <p className="text-foreground mt-1 flex items-center gap-2">
                          <Mail size={14} />
                          {profile.email || "-"}
                        </p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-500 dark:text-slate-400">
                          {t("profile.phone")}
                        </Label>
                        <p className="text-foreground mt-1 flex items-center gap-2">
                          <Phone size={14} />
                          {profile.phone || "-"}
                        </p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-500 dark:text-slate-400">
                          {t("profile.address")}
                        </Label>
                        <p className="text-foreground mt-1 flex items-start gap-2">
                          <MapPin size={14} className="mt-0.5 flex-shrink-0" />
                          <span className="line-clamp-2">{profile.address || "-"}</span>
                        </p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-500 dark:text-slate-400">
                          {t("profile.licenseNumber")}
                        </Label>
                        <p className="text-foreground mt-1 flex items-center gap-2">
                          <FileText size={14} />
                          {(profile as any).license_number || "-"}
                        </p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-500 dark:text-slate-400">
                          {t("profile.taxId")}
                        </Label>
                        <p className="text-foreground mt-1 flex items-center gap-2">
                          <Hash size={14} />
                          {(profile as any).tax_id || "-"}
                        </p>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-sm font-medium text-slate-500 dark:text-slate-400">
                          {t("profile.companyDescription")}
                        </Label>
                        <p className="text-foreground mt-1 line-clamp-3">
                          {profile.description || "-"}
                        </p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-500 dark:text-slate-400">
                          {t("profile.status")}
                        </Label>
                        <div className="mt-1">{getStatusBadge(profile.status || 'pending')}</div>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-500 dark:text-slate-400">
                          {t("profile.createdAt")}
                        </Label>
                        <p className="text-foreground mt-1 flex items-center gap-2 text-sm">
                          <CalendarIcon size={14} />
                          {profile.created_at ? format(new Date(profile.created_at), "PPP", { locale: language === "ar" ? ar : enUS }) : "-"}
                        </p>
                      </div>
                    </div>
                  ) : null}
                </CardContent>
              </Card>

              {/* Specializations Card */}
              <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg flex items-center gap-2 text-slate-900 dark:text-slate-100">
                    <Briefcase size={20} className="text-foreground" />
                    {t("profile.specializations") || "التخصصات"} ({profile?.specializations?.length || 0})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isLoading ? (
                    <div className="space-y-3">
                      <Skeleton className="h-16 w-full" />
                      <Skeleton className="h-16 w-full" />
                    </div>
                  ) : profile?.specializations && profile.specializations.length > 0 ? (
                    <div className="space-y-3">
                      {profile.specializations.map((spec: any) => (
                        <div key={spec.id} className="flex items-center gap-3 p-3 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                          <div className="w-10 h-10 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center">
                            <Briefcase size={16} className="text-foreground" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 dark:text-slate-100">
                              {typeof spec.name === "string" ? spec.name : spec.name[language] || spec.name.en}
                            </p>
                            {spec.description && (
                              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                                {typeof spec.description === "string" 
                                  ? spec.description 
                                  : spec.description?.[language] || spec.description?.en || ''}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                      <Briefcase size={48} className="mx-auto mb-4 opacity-50 text-foreground" />
                      <p>{t("profile.noSpecializations") || "لا توجد تخصصات"}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Edit Profile Tab */}
          <TabsContent value="edit" className="space-y-6">
            <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2 text-slate-900 dark:text-slate-100">
                  <Edit size={20} className="text-foreground" />
                  {t("profile.editProfile")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="flex justify-center mb-6">
                    <div className="relative">
                      <Avatar className="w-24 h-24">
                        <AvatarImage src={profileImage ? URL.createObjectURL(profileImage) : (profile?.profile_image ? `https://maroon-grouse-638711.hostingersite.com/${(profile as any).profile_image}` : undefined)} alt="Profile" />
                        <AvatarFallback className="text-2xl font-bold bg-primary text-white">
                          {(formData.company_name || profile?.company_name || t("profile.companyName")).charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <label className="absolute bottom-0 right-0 w-8 h-8 bg-secondary rounded-full flex items-center justify-center text-secondary-foreground cursor-pointer hover:bg-secondary/80 transition-colors">
                        <Camera size={16} />
                        <input type="file" accept="image/*" onChange={handleProfileImageChange} className="hidden" />
                      </label>
                    </div>
                  </div>
                  
                  {profileImage && <p className="text-sm text-muted-foreground text-center">{t("profile.selectedImage") || "الصورة المحددة"}: {profileImage.name}</p>}

                  {isLoading ? (
                    <div className="space-y-4">
                      <Skeleton className="h-10 w-full" />
                      <Skeleton className="h-10 w-full" />
                      <Skeleton className="h-10 w-full" />
                    </div>
                  ) : (
                    <>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-foreground mb-2 block">{t("profile.companyName")} *</Label>
                          <Input value={formData.company_name} onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} disabled={updateProfile.isPending} required />
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-foreground mb-2 block">{t("profile.email") || "البريد الإلكتروني"}</Label>
                          <Input type="email" value={formData.email} disabled className="bg-muted" />
                          <p className="text-xs text-muted-foreground mt-1">{t("profile.emailReadOnly") || "لا يمكن تغيير البريد الإلكتروني"}</p>
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-foreground mb-2 block">{t("profile.phone")} *</Label>
                          <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} disabled={updateProfile.isPending} required />
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-foreground mb-2 block">{t("profile.address")}</Label>
                          <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} disabled={updateProfile.isPending} />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-foreground mb-2 block">{t("profile.licenseNumber")}</Label>
                          <Input value={formData.license_number} onChange={(e) => setFormData({ ...formData, license_number: e.target.value })} disabled={updateProfile.isPending} />
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-foreground mb-2 block">{t("profile.taxId")}</Label>
                          <Input value={formData.tax_id} onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })} disabled={updateProfile.isPending} />
                        </div>
                      </div>

                      <div className="space-y-2 pt-2">
                        <Label className="text-sm font-medium text-foreground mb-2 block">{t("profile.specializations") || "التخصصات"}</Label>
                        <SearchableMultiSelect
                          options={profile?.available_specializations || []}
                          selectedIds={formData.specialization_ids}
                          onChange={(selectedIds) => setFormData({ ...formData, specialization_ids: selectedIds })}
                          placeholder={t("profile.selectSpecializations") || "اختر التخصصات..."}
                          searchPlaceholder={t("profile.searchSpecializations") || "ابحث عن التخصصات..."}
                          noOptionsText={t("profile.noSpecializationsFound") || "لم يتم العثور على تخصصات"}
                          maxVisible={3}
                          disabled={updateProfile.isPending || isLoading}
                        />
                      </div>
                    </>
                  )}

                  <div>
                    <Label className="text-sm font-medium text-foreground mb-2 block">{t("profile.companyDescription")}</Label>
                    <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={4} disabled={updateProfile.isPending || isLoading} />
                  </div>

                  <ReadOnlyWrapper>
                    <Button type="submit" size="lg" disabled={updateProfile.isPending || isLoading} className="w-full sm:w-auto">
                      {updateProfile.isPending ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />{t("common.loading") || "جاري الحفظ..."}</> : <><Upload className="w-4 h-4 mr-2" />{t("profile.saveChanges")}</>}
                    </Button>
                  </ReadOnlyWrapper>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="space-y-6">
            <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2 text-slate-900 dark:text-slate-100">
                  <FileText size={20} className="text-foreground" />
                  {t("profile.documents")} ({documents.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isLoading ? [1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full rounded-lg" />) : documents.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4 text-sm">{t("profile.noDocuments") || "لا توجد مستندات"}</p>
                ) : documents.map((doc: any) => (
                  <div key={doc.id} className="flex items-center justify-between p-4 bg-muted rounded-lg mb-3">
                    <div className="flex items-center gap-3">
                      <FileText size={20} className={doc.status === "approved" ? "text-green-500" : "text-primary"} />
                      <div>
                        <p className="font-medium text-foreground text-sm">{getDocumentTypeName(doc)}</p>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            doc.status === "approved" 
                              ? "bg-green-100 text-green-700" 
                              : doc.status === "rejected"
                              ? "bg-red-100 text-red-700"
                              : "bg-amber-100 text-amber-700"
                          }`}>
                            {doc.status === "approved" 
                              ? (t("profile.approved") || "معتمد") 
                              : doc.status === "rejected"
                              ? (t("profile.rejected") || "مرفوض")
                              : (t("profile.pending") || "قيد المراجعة")
                            }
                          </span>
                          {doc.status === "approved" && (
                            <span className="text-xs text-green-600">
                              <Check size={12} className="inline mr-1" />
                              {t("profile.approvedByAdmin") || "معتمد من الإدارة"}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <ReadOnlyWrapper>
                      {doc.status !== "approved" && (
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteDocument(doc.id)} disabled={deleteDocument.isPending}>
                          <Trash2 size={16} className="text-destructive" />
                        </Button>
                      )}
                      {doc.status === "approved" && (
                        <span className="text-xs text-muted-foreground px-2">
                          {t("profile.cannotDeleteApproved") || "لا يمكن الحذف"}
                        </span>
                      )}
                    </ReadOnlyWrapper>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Upload New Documents Card */}
            <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2 text-slate-900 dark:text-slate-100">
                  <Upload size={20} className="text-foreground" />
                  {t("profile.uploadDocuments") || " raise documents"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Commercial Register */}
                  <div className="flex items-center justify-between p-4 border border-slate-200 dark:border-slate-600 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText size={20} className="text-primary" />
                      <div>
                        <p className="font-medium text-foreground text-sm">{t("documents.commercialRegister") || "Commercial Register"}</p>
                        <p className="text-xs text-muted-foreground">{t("profile.optional") || "Optional"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {commercialRegister && (
                        <span className="text-xs text-green-600 flex items-center gap-1">
                          <Check size={12} />
                          {commercialRegister.name}
                        </span>
                      )}
                      <label className="cursor-pointer">
                        <Input
                          id="commercial-register-input"
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleCommercialRegisterChange}
                          className="hidden"
                          disabled={updateProfile.isPending}
                        />
                        <Button type="button" variant="outline" size="sm" asChild disabled={updateProfile.isPending}>
                          <span>{t("common.browse") || "Browse"}</span>
                        </Button>
                      </label>
                      {commercialRegister && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => clearDocumentFile(setCommercialRegister, 'commercial-register-input')}
                          disabled={updateProfile.isPending}
                        >
                          <X size={14} />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Tax Certificate */}
                  <div className="flex items-center justify-between p-4 border border-slate-200 dark:border-slate-600 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText size={20} className="text-primary" />
                      <div>
                        <p className="font-medium text-foreground text-sm">{t("documents.taxCertificate") || "Tax Certificate"}</p>
                        <p className="text-xs text-muted-foreground">{t("profile.optional") || "Optional"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {taxCertificate && (
                        <span className="text-xs text-green-600 flex items-center gap-1">
                          <Check size={12} />
                          {taxCertificate.name}
                        </span>
                      )}
                      <label className="cursor-pointer">
                        <Input
                          id="tax-certificate-input"
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleTaxCertificateChange}
                          className="hidden"
                          disabled={updateProfile.isPending}
                        />
                        <Button type="button" variant="outline" size="sm" asChild disabled={updateProfile.isPending}>
                          <span>{t("common.browse") || "Browse"}</span>
                        </Button>
                      </label>
                      {taxCertificate && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => clearDocumentFile(setTaxCertificate, 'tax-certificate-input')}
                          disabled={updateProfile.isPending}
                        >
                          <X size={14} />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* IBAN Certificate */}
                  <div className="flex items-center justify-between p-4 border border-slate-200 dark:border-slate-600 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText size={20} className="text-primary" />
                      <div>
                        <p className="font-medium text-foreground text-sm">{t("documents.ibanCertificate") || "IBAN Certificate"}</p>
                        <p className="text-xs text-muted-foreground">{t("profile.optional") || "Optional"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {ibanCertificate && (
                        <span className="text-xs text-green-600 flex items-center gap-1">
                          <Check size={12} />
                          {ibanCertificate.name}
                        </span>
                      )}
                      <label className="cursor-pointer">
                        <Input
                          id="iban-certificate-input"
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleIbanCertificateChange}
                          className="hidden"
                          disabled={updateProfile.isPending}
                        />
                        <Button type="button" variant="outline" size="sm" asChild disabled={updateProfile.isPending}>
                          <span>{t("common.browse") || "Browse"}</span>
                        </Button>
                      </label>
                      {ibanCertificate && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => clearDocumentFile(setIbanCertificate, 'iban-certificate-input')}
                          disabled={updateProfile.isPending}
                        >
                          <X size={14} />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Zakat Income Certificate */}
                  <div className="flex items-center justify-between p-4 border border-slate-200 dark:border-slate-600 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText size={20} className="text-primary" />
                      <div>
                        <p className="font-medium text-foreground text-sm">{t("documents.zakatCertificate") || "Zakat & Income Certificate"}</p>
                        <p className="text-xs text-muted-foreground">{t("profile.optional") || "Optional"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {zakatIncomeCertificate && (
                        <span className="text-xs text-green-600 flex items-center gap-1">
                          <Check size={12} />
                          {zakatIncomeCertificate.name}
                        </span>
                      )}
                      <label className="cursor-pointer">
                        <Input
                          id="zakat-income-certificate-input"
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleZakatIncomeCertificateChange}
                          className="hidden"
                          disabled={updateProfile.isPending}
                        />
                        <Button type="button" variant="outline" size="sm" asChild disabled={updateProfile.isPending}>
                          <span>{t("common.browse") || "Browse"}</span>
                        </Button>
                      </label>
                      {zakatIncomeCertificate && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => clearDocumentFile(setZakatIncomeCertificate, 'zakat-income-certificate-input')}
                          disabled={updateProfile.isPending}
                        >
                          <X size={14} />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Content Certificate (replacing Social Insurance) */}
                  <div className="flex items-center justify-between p-4 border border-slate-200 dark:border-slate-600 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText size={20} className="text-primary" />
                      <div>
                        <p className="font-medium text-foreground text-sm">{t("documents.contentCertificate") || "Content Certificate"}</p>
                        <p className="text-xs text-muted-foreground">{t("profile.optional") || "Optional"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {socialInsuranceCertificate && (
                        <span className="text-xs text-green-600 flex items-center gap-1">
                          <Check size={12} />
                          {socialInsuranceCertificate.name}
                        </span>
                      )}
                      <label className="cursor-pointer">
                        <Input
                          id="social-insurance-certificate-input"
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleSocialInsuranceCertificateChange}
                          className="hidden"
                          disabled={updateProfile.isPending}
                        />
                        <Button type="button" variant="outline" size="sm" asChild disabled={updateProfile.isPending}>
                          <span>{t("common.browse") || "Browse"}</span>
                        </Button>
                      </label>
                      {socialInsuranceCertificate && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => clearDocumentFile(setSocialInsuranceCertificate, 'social-insurance-certificate-input')}
                          disabled={updateProfile.isPending}
                        >
                          <X size={14} />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Company Profile */}
                  <div className="flex items-center justify-between p-4 border border-slate-200 dark:border-slate-600 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText size={20} className="text-primary" />
                      <div>
                        <p className="font-medium text-foreground text-sm">{t("documents.companyProfile") || "Company Profile"}</p>
                        <p className="text-xs text-muted-foreground">{t("profile.optional") || "Optional"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {companyProfile && (
                        <span className="text-xs text-green-600 flex items-center gap-1">
                          <Check size={12} />
                          {companyProfile.name}
                        </span>
                      )}
                      <label className="cursor-pointer">
                        <Input
                          id="company-profile-input"
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleCompanyProfileChange}
                          className="hidden"
                          disabled={updateProfile.isPending}
                        />
                        <Button type="button" variant="outline" size="sm" asChild disabled={updateProfile.isPending}>
                          <span>{t("common.browse") || "Browse"}</span>
                        </Button>
                      </label>
                      {companyProfile && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => clearDocumentFile(setCompanyProfile, 'company-profile-input')}
                          disabled={updateProfile.isPending}
                        >
                          <X size={14} />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="pt-4">
                    <Button
                      type="submit"
                      size="lg"
                      disabled={
                        updateProfile.isPending ||
                        (!commercialRegister && !taxCertificate && !ibanCertificate && !zakatIncomeCertificate && !socialInsuranceCertificate && !companyProfile)
                      }
                      className="w-full sm:w-auto"
                    >
                      {updateProfile.isPending ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          {t("common.loading") || "Saving..."}
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          {t("profile.uploadDocuments") || "Upload Documents"}
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Password Tab */}
          <TabsContent value="password" className="space-y-6">
            <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2 text-slate-900 dark:text-slate-100">
                  <Lock size={20} className="text-foreground" />
                  {t("profile.changePassword") || "تغيير كلمة المرور"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePasswordChange} className="space-y-6">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium text-foreground mb-2 block">{t("profile.currentPassword") || "كلمة المرور الحالية"}</Label>
                      <div className="relative">
                        <Input type={showPasswords.current ? "text" : "password"} value={passwordData.current_password} onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })} disabled={changePassword.isPending} placeholder={t("profile.enterCurrentPassword") || "أدخل كلمة المرور الحالية"} />
                        <Button type="button" variant="ghost" size="icon" className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8" onClick={() => togglePasswordVisibility("current")}>
                          {showPasswords.current ? <EyeOff size={16} /> : <Eye size={16} />}
                        </Button>
                      </div>
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-foreground mb-2 block">{t("profile.newPassword") || "كلمة المرور الجديدة"}</Label>
                      <div className="relative">
                        <Input type={showPasswords.new ? "text" : "password"} value={passwordData.password} onChange={(e) => setPasswordData({ ...passwordData, password: e.target.value })} disabled={changePassword.isPending} placeholder={t("profile.enterNewPassword") || "أدخل كلمة المرور الجديدة"} />
                        <Button type="button" variant="ghost" size="icon" className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8" onClick={() => togglePasswordVisibility("new")}>
                          {showPasswords.new ? <EyeOff size={16} /> : <Eye size={16} />}
                        </Button>
                      </div>
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-foreground mb-2 block">{t("profile.confirmNewPassword") || "تأكيد كلمة المرور الجديدة"}</Label>
                      <div className="relative">
                        <Input type={showPasswords.confirmation ? "text" : "password"} value={passwordData.password_confirmation} onChange={(e) => setPasswordData({ ...passwordData, password_confirmation: e.target.value })} disabled={changePassword.isPending} placeholder={t("profile.confirmNewPassword") || "أكد كلمة المرور الجديدة"} />
                        <Button type="button" variant="ghost" size="icon" className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8" onClick={() => togglePasswordVisibility("confirmation")}>
                          {showPasswords.confirmation ? <EyeOff size={16} /> : <Eye size={16} />}
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <ReadOnlyWrapper>
                      <Button type="submit" size="lg" disabled={changePassword.isPending}>
                        {changePassword.isPending ? (t("common.loading") || "جاري الحفظ...") : (t("profile.changePassword") || "تغيير كلمة المرور")}
                      </Button>
                    </ReadOnlyWrapper>
                    <Button type="button" variant="outline" size="lg" onClick={() => setPasswordData({ current_password: "", password: "", password_confirmation: "" })} disabled={changePassword.isPending}>
                      {t("common.cancel") || "إلغاء"}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Profile;
